// import Link from '@/components/link-component'
import React from 'react'
import PageWrapper from '@/components/page-wrapper'
import SocialShare from '@/components/social-share'
import RichText from '@/components/ui/rich-text'
import ArticleHeader from '@/components/article-header'
import ArticleBreadcrumb from '@/components/article-breadcrumb'
import Code from '@/components/ui/code/code'
import Image from '@/components/ui/image'
import Video from '@/components/ui/video'
import TocNav from '@/components/toc-nav'
import Link from '@/components/link-component'
import SectionCta from '@/components/section/cta'

import Table from '@/components/table'
import { slugify } from '@/utils/strings'

import { TypeBlogPage, TypeSectionCta, type TypeSeo } from '@/types/ctf'
import Lottie from '@/components/lottie'
import { localizedPath, useLocale } from '@/utils/locale'
import { usePage } from '@inertiajs/react'
import CtaLink from '@/components/ui/cta-link.tsx'

type BlogPageProps = {
  post: TypeBlogPage<'WITHOUT_UNRESOLVABLE_LINKS'>
  defaultCta: TypeSectionCta<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerNav: any
  footerNav: any
  headerBanners: any
  newsletterForm: any
  seo: any
  toc: any
}

export default function BlogPage({
  post: { fields },
  defaultCta,
  headerNav,
  footerNav,
  headerBanners,
  newsletterForm,
  toc,
}: BlogPageProps) {
  const page = usePage()
  const { currentLocale } = useLocale(page.url)

  const {
    title,
    slug,
    author,
    blogAuthor,
    description,
    publishDate,
    updateDate,
    heroImageGradientOverlay,
    heroImage,
    heroBackgroundColor = 'Midnight',
    ogImage,
    content,
    cta,
    tags,
    seo: _seo,
  } = fields
  const breadcrumb = [{ name: 'Blog', path: '/blog/' }, { name: title }]
  const seo = _seo
    ? ({
        fields: {
          title: _seo.fields?.title || title,
          description: _seo.fields?.description || description,
          ogImage: _seo.fields?.ogImage || ogImage,
        },
      } as TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>)
    : ({ fields: { title, description, ogImage } } as TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>)

  return (
    <PageWrapper
      bodyTw="bg-secondary-50 dark:bg-secondary-900"
      {...({
        seo: { ...seo },
        breadcrumb,
        headerNav,
        footerNav,
        headerBanners,
        newsletterForm,
      } as any)}
    >
      <>
        <ArticleHeader
          author={blogAuthor?.fields ? blogAuthor : author}
          image={heroImage}
          imageOverlay={heroImageGradientOverlay}
          tagline="The Bitwarden Blog"
          publishDate={publishDate}
          updateDate={updateDate}
          title={title}
          backgroundColor={heroBackgroundColor}
        />
        <article className="mx-auto max-w-8xl md:px-5 lg:px-9">
          <div className="lg:flex space-x-10 py-10 px-5 xl:px-0">
            <div className="max-w-[740px]">
              <ArticleBreadcrumb breadcrumb={breadcrumb} />
              {content?.map((item: any) => {
                const contentType = item?.sys?.contentType?.sys?.id
                if (contentType === 'componentRichText')
                  return (
                    <section key={item.sys.id}>
                      <RichText body={item.fields.body} />
                    </section>
                  )
                if (contentType === 'sectionCta') {
                  return <SectionCta key={item.sys.id} {...item.fields} className="!px-0 mb-10" />
                }
                if (contentType === 'componentCode') {
                  return <Code key={item.sys.id} body={item.fields.body} language={item.fields.language} />
                }
                if (contentType === 'resourceImage') {
                  return <Image key={item.sys.id} {...item.fields} />
                }
                if (contentType === 'googlePresentation')
                  return (
                    <div key={item.sys.id} className="relative aspect-video">
                      <iframe
                        title={title}
                        src={item.fields.url}
                        width="100%"
                        height="100%"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                        sandbox="allow-scripts allow-popups"
                      />
                    </div>
                  )
                if (contentType === 'componentVideo')
                  return (
                    <div key={item.sys.id} className="my-10">
                      <Video {...item} />
                    </div>
                  )

                if (contentType === 'componentTable')
                  return (
                    <div key={item.sys.id} className="my-10">
                      <Table {...item.fields} />
                    </div>
                  )

                if (contentType === 'componentLottie')
                  return <Lottie key={item.sys.id} {...item.fields} className="my-10" />

                return null
              })}

              {tags && tags.length > 0 && (
                <div className="flex flex-wrap pt-10 items-center gap-2">
                  <span className="font-bwi mr-2">&#xe95e;</span>
                  {tags.map((tag) => (
                    <CtaLink
                      key={tag}
                      to={localizedPath(`/blog/tag/${slugify(tag)}/`, currentLocale)}
                      style="Button: Outlined"
                      size="Smaller"
                    >
                      {tag}
                    </CtaLink>
                  ))}
                </div>
              )}

              <div className="md:flex flex-row-reverse items-center justify-between lg:block my-10">
                <div className="block lg:hidden mb-8 md:mb-0">
                  <SocialShare title={title} slug={`/blog/${slug}`} />
                </div>
                <Link to="/blog/">
                  <span className="font-bwi mr-4">&#xe96b;</span>
                  {'Back to Blog'}
                </Link>
              </div>
            </div>

            <div className="hidden lg:block flex-grow">
              <div className="sticky top-24 overflow-y-auto px-8 py-8 bg-white rounded-lg border-b-2 border-secondary-100 dark:bg-secondary-800">
                <SocialShare title={title} slug={`/blog/${slug}`} />

                {toc && toc.length > 0 && (
                  <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
                    <TocNav id="blog-page-nav" sections={toc} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>

        <div className="pb-20">
          {cta ? <SectionCta {...(cta.fields as any)} /> : <SectionCta {...defaultCta.fields} />}
        </div>
      </>
    </PageWrapper>
  )
}
